import { useState } from 'react';
import type { BannerPublic, PullResult } from '@tarot/contracts';
import { ApiError } from '@/api/client';
import { useBuy, useMe, usePull, useShop } from '@/api/hooks';
import { useUi } from '@/app/store';
import { ItemPreview } from '@/components/Table';
import { Badge, Skeleton } from '@/components/ui';
import { platform } from '@/platform';
import { usePaywall } from '../paywall/Paywall';
import { PullOverlay } from './PullOverlay';
import { Icon } from '@/components/Icon';
import { CardFan } from '@/components/CardFan';
import { useDecks } from '@/api/hooks';

type Filter = 'all' | 'cloth' | 'pattern' | 'candles' | 'figure' | 'deck';
const FILTERS: [Filter, string][] = [['all', 'Все'], ['cloth', 'Скатерти'], ['pattern', 'Узоры'], ['candles', 'Свечи'], ['figure', 'Статуэтки'], ['deck', 'Колоды']];

export function ShopScreen() {
  const { data: me } = useMe();
  const { data: shop } = useShop();
  const { data: decksData } = useDecks();
  const pull = usePull();
  const buy = useBuy();
  const toast = useUi((s) => s.showToast);
  const { paywall, openPaywall } = usePaywall();
  const [filter, setFilter] = useState<Filter>('all');
  const [results, setResults] = useState<PullResult[] | null>(null);

  if (!me || !shop) return <div className="screen"><Skeleton h={200} /></div>;
  const rub = (shop.providers ?? []).includes('robokassa');

  const goods = [
    ...shop.items.map((i) => ({ ...i, kind: 'item' as const, slotKey: i.slot as Filter })),
    ...shop.decks.filter((d) => d.priceCrystals !== null).map((d) => ({ ...d, kind: 'deck' as const, slotKey: 'deck' as Filter })),
  ].filter((g) => filter === 'all' || g.slotKey === filter);

  const onPull = async (b: BannerPublic, count: 1 | 10, useTicket: boolean) => {
    try {
      platform.haptic('medium');
      const r = await pull.mutateAsync({ bannerId: b.id, count, useTicket });
      setResults(r.results);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'INSUFFICIENT_FUNDS') await openPaywall('funds', true);
      else toast((e as Error).message, true);
    }
  };

  const onBuy = async (kind: 'item' | 'deck', id: string, title: string) => {
    try {
      await buy.mutateAsync({ kind, id });
      platform.haptic('success');
      toast(`«${title}» — в инвентаре`);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'INSUFFICIENT_FUNDS') await openPaywall('funds', true);
      else toast((e as Error).message, true);
    }
  };

  return (
    <div className="screen">
      <div className="topbar">
        <div className="title">Магазин</div>
        <div className="wallet">
          <span className="pill"><Icon name="crystal" size={16} className="c-crystal" />{me.wallet.crystals}</span>
          <span className="pill"><Icon name="shard" size={16} className="c-shard" />{me.wallet.shards}</span>
          {me.wallet.tickets > 0 && <span className="pill gold"><Icon name="ticket" size={16} />{me.wallet.tickets}</span>}
        </div>
      </div>
      <p className="muted" style={{ marginTop: -6 }}>Украсьте свой стол для гаданий</p>

      <div className="stack">
        {shop.banners.map((b) => {
          const deckId = b.rateUp.find((r) => r.startsWith('deck:'))?.slice(5);
          const withImages = decksData?.decks.filter((d) => d.cards.some((c) => c.imageUrl)) ?? [];
          const deck = withImages.find((d) => d.id === deckId) ?? withImages[0];
          const offset = b.type === 'event' ? 16 : 6;
          const imgs = deck?.cards.filter((c) => c.imageUrl).slice(offset, offset + 3).map((c) => c.imageUrl) ?? [];
          return <Banner key={b.id} banner={b} tickets={me.wallet.tickets} onPull={onPull} busy={pull.isPending} art={<CardFan images={imgs} back={deck?.backStyle} size={56} />} />;
        })}

        <h2 style={{ marginTop: 6 }}>Товары</h2>
        <div className="chips">
          {FILTERS.map(([k, t]) => <button key={k} className={`chip${filter === k ? ' active' : ''}`} onClick={() => setFilter(k)}>{t}</button>)}
        </div>
        <div className="grid2">
          {goods.map((g) => (
            <div key={g.id} className={`item ${g.rarity}${g.owned ? ' owned' : ''}`}>
              <div className="preview"><ItemPreview item={g.kind === 'deck' ? { backStyle: g.backStyle } : g} /></div>
              <Badge rarity={g.rarity} />
              <div className="t">{g.title}</div>
              <div className="d">{g.description}</div>
              <div className="price">
                <span className={g.rarity === 'rare' ? 'pill gold' : 'pill'}><Icon name="crystal" size={14} className="c-crystal" />{g.priceCrystals}</span>
                {g.owned ? <span className="dim">Есть</span> : <button className="btn ghost sm" disabled={buy.isPending} onClick={() => onBuy(g.kind, g.id, g.title)}>Купить</button>}
              </div>
            </div>
          ))}
        </div>

        <h2>Кристаллы и Premium</h2>
        <div className="card lg premium-card">
          <div className="row spread"><h3><Icon name="sparkle" size={18} filled />Premium</h3>{me.premium && <span className="pill gold"><Icon name="check" size={14} />Активен</span>}</div>
          <ul><li>Безлимит раскладов и дополнений</li><li>300 кристаллов каждый месяц</li><li>Приоритет в очереди</li></ul>
          <button className="btn warm" onClick={() => void openPaywall('premium')}>{me.premium ? 'Продлить' : 'Оформить'}</button>
        </div>
        {shop.products.filter((p) => p.type === 'crystals').map((p) => (
          <div key={p.id} className="card product"><b><Icon name="crystal" size={18} className="c-crystal" />{p.title}</b><button className="btn ghost sm" onClick={() => void openPaywall('funds', true)}>{rub && p.priceRub !== null ? `${p.priceRub} ₽` : `⭐ ${p.priceStars}`}</button></div>
        ))}
        <p className="dim center">Шансы гачи публикуются на баннере. Оплата — {rub ? 'картой через Robokassa' : 'Telegram Stars'}.</p>
      </div>

      {results && <PullOverlay results={results} onClose={() => setResults(null)} />}
      {paywall}
    </div>
  );
}

function Banner({ banner: b, tickets, onPull, busy, art }: { banner: BannerPublic; tickets: number; onPull: (b: BannerPublic, c: 1 | 10, t: boolean) => void; busy: boolean; art?: React.ReactNode }) {
  const useTicket = tickets > 0;
  return (
    <div className={`banner ${b.type}`}>
      <div className="art">{art}</div>
      <span className="tag">{b.type === 'event' ? <><Icon name="flame" size={12} filled />Повышенный шанс</> : <><Icon name="star" size={12} filled />Стандарт</>}</span>
      <h3>{b.title}</h3>
      <div className="muted" style={{ fontSize: 13 }}>{b.subtitle}</div>
      <div className="rates">
        <span><i style={{ background: '#c084fc' }} />Обычный {b.rates.common}%</span>
        <span><i style={{ background: '#f5c242' }} />Золотой {b.rates.rare}%</span>
        <span><i style={{ background: '#fca5a5' }} />Ивентовый {b.rates.event}%</span>
      </div>
      <div className="dim" style={{ color: 'rgba(255,255,255,.7)', marginBottom: 8 }}>До гаранта: золотой {b.pity.toRare}, ивентовый {b.pity.toEvent}</div>
      <div className="btns">
        <button className="btn" disabled={busy} onClick={() => onPull(b, 1, useTicket)}>{useTicket ? <><Icon name="ticket" size={16} />Крутить ×1</> : <>Крутить ×1<span className="price"><Icon name="crystal" size={14} />{b.price1}</span></>}</button>
        <button className="btn" disabled={busy} onClick={() => onPull(b, 10, false)}>×10<span className="price"><Icon name="crystal" size={14} />{b.price10}</span></button>
      </div>
    </div>
  );
}
